import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Spinner } from "reactstrap";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import {
  showToastMessage,
  formatTime24Hour,
} from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CheckInModal, { CheckInForm } from "Components/Common/CheckinModal";
import DeleteModal from "Components/Common/DeleteModal";
import CommonButton from "Components/Common/CommonButton";
import PageTitle from "Components/Common/PageTitle";
import { useSubjectCheckin } from "modules/Clinical/CheckinCheckout/api/useSubjectCheckin";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { queryClient } from "api/queryClient";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import { loadCodelistsDetailId } from "api/modules/common.option";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { usePermission } from "context/PermissionContext";
import { BreadcrumbContext } from "Layouts/Header";
import { STUDY_PATHS, SUBJECT_CHECKIN_PATHS } from "Routes/paths";
import StatCard from "Components/Common/StatCard";
import { CheckInColumns } from "../helpers/subjectcheckin.constant";
import { CheckStatus, SubjectRow } from "../helpers/subjectcheckin.types";

export interface SubjectCheckinListProps {
  renderHeaderTabs?: React.ReactNode;
  hideHeader?: boolean;
}

const SubjectCheckinList: React.FC<SubjectCheckinListProps> = ({ renderHeaderTabs, hideHeader = false }) => {
  document.title = "Subject Check-In | ZENTIXS VISTA";

  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const studyId = Number(sessionStorage.getItem("activestudyid"));
  const [checklisttype] = useState<string>("Subject Check-In");

  const { SelectQuery: StudyDetailsQuery } = useMasterStudyService({
    id: studyId,
    enabledSelect: true,
  });

  const studyData = StudyDetailsQuery?.data?.data?.[0];

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Study", link: STUDY_PATHS.LIST },
      { text: "Check-In" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const [codelistsLoaded, setCodelistsLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      await loadCodelistsDetailId(["Checklist_Type_OPTIONS"]);
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  const { SelectAllQuery: ChecklistQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: checklisttype,
  });

  const checkinQuestions = useMemo(() => {
    return ChecklistQuery?.data?.data ?? [];
  }, [ChecklistQuery?.data?.data]);

  const dynamicPeriods = useMemo(() => {
    const num = parseInt(studyData?.numberofperiods || "0", 10);
    if (isNaN(num) || num <= 0) return [{ key: "1", label: "Period 01" }];

    return Array.from({ length: num }, (_, i) => {
      const periodNum = (i + 1).toString().padStart(2, "0");
      return {
        key: `${i + 1}`,
        label: `Period ${periodNum}`,
      };
    });
  }, [studyData?.numberofperiods]);

  const [activeTab, setActiveTab] = useState("1");
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);

  // Modals state
  const [checkinOpen, setCheckinOpen] = useState<boolean>(false);
  const [activeSubject, setActiveSubject] = useState<SubjectRow | null>(null);

  // Delete action state
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [recordToDelete, setRecordToDelete] = useState<SubjectRow | null>(null);

  // Audit
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const PAGE_SIZE = 5;

  const currentPeriod = useMemo(() => {
    if (!studyData?.periods) return null;
    return studyData.periods.find(
      (p: any) => String(p.period_no) === activeTab,
    );
  }, [studyData?.periods, activeTab]);

  const currentPeriodId = currentPeriod?.period_id || null;

  const { SelectAllQuery, UpdateMutation, DeleteMutation } = useSubjectCheckin(
    {
      searchText,
      pageNumber: currentPageNumber,
      pageSize: PAGE_SIZE,
      periodId: currentPeriodId,
      studyId: studyId,
    },
  );

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
  };

  const data: SubjectRow[] = useMemo(() => {
    return (queryResponse?.data ?? []).map((row: any) => {
      const getCheckinStatus = (status: string, date: any): CheckStatus => {
        const low = (status ?? "").trim().toLowerCase();

        if (low.includes("pending")) return "pending";
        if (low.includes("checked") && low.includes("in")) return "checked-in";
        if (low.includes("completed")) return "checked-in";

        if (date) return "checked-in";
        return "pending";
      };

      const checkStatus = getCheckinStatus(row.checkinstatus, row.checkindate);

      return {
        id: row.subjectcheckinid ?? row.id,
        subjectNo: String(row.subjectno ?? ""),
        subjectName: row.subjectname ?? "",
        initials: row.initials ?? "",
        allocatedlockerno: row.allocatedlockerno ?? row.lockerno ?? row.allocated_locker_no ?? null,
        checkinStatus: checkStatus,
        checkinDate: row.checkindate || null,
        checkinTime: row.checkintime ? formatTime24Hour(row.checkintime.split("+")[0]) : null,
        checkoutStatus: "pending",
        checkoutDate: null,
        checkoutTime: null,
        total_count: row.total_count ?? 0,
      };
    });
  }, [queryResponse?.data]);

  const stats = useMemo(
    () => ({
      total: data.length,
      checkedIn: data.filter((s) => s.checkinStatus === "checked-in").length,
      checkedOut: 0,
      pending: data.filter((s) => s.checkinStatus === "pending").length,
    }),
    [data],
  );

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setCurrentPageNumber(1);
  };

  const openCheckin = (row: SubjectRow) => {
    setActiveSubject(row);
    setCheckinOpen(true);
  };

  const confirmCheckin = (form: CheckInForm) => {
    if (!activeSubject) return;

    UpdateMutation.mutate(
      {
        subjectcheckinid: activeSubject.id,
        checkinstatus: "Checked In",
        checkindate: form.checkinDate,
        checkintime: form.checkinTime,
        allocatedlockerno: form.allocatedlockerno || null,
        verificationpin: form.verificationpin,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res?.message ||
            `${activeSubject.subjectName} checked in successfully`,
          );
          queryClient.invalidateQueries({
            queryKey: ["subjectcheckin-selectAll"],
          });
          setCheckinOpen(false);
          setActiveSubject(null);
        },
        onError: (error: any) =>
          showToastMessage("error", error),
      },
    );
  };

  const handleDeleteConfirm = (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (!recordToDelete) return;

    DeleteMutation.mutate(
      {
        subjectcheckinid: recordToDelete.id,
        updatereason: payload.reason,
      },
      {
        onSuccess: (res: any) => {
          showToastMessage(
            "success",
            res?.message || "Record deleted successfully",
          );
          queryClient.invalidateQueries({
            queryKey: ["subjectcheckin-selectAll"],
          });
          setCurrentPageNumber(1);
          setDeleteModalOpen(false);
          setShowDeleteReason(false);
          setRecordToDelete(null);
        },
        onError: (error: any) =>
          showToastMessage("error", error?.message || "Delete failed"),
      },
    );
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Subject Check-In",
      studyid: studyId,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File(
            [data],
            "Subject_Checkin_Audit_Log.pdf",
            { type: "application/pdf" },
          );
          setPreviewFile(pdfFile);
          setPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const { hasPermission } = usePermission();
  const canassign = hasPermission(PERMISSION_MODULES.SUBJECT_CHECKIN, PERMISSION_ACTIONS.CHECK_IN);

  return (
    <div>
      <div className={styles.dashboardContainer} style={{ paddingTop: "0px" }}>
        <Container fluid className="px-0">
          {/* Page Header */}
          <div className="mb-2 px-0">
            <div className="d-flex justify-content-between align-items-center mt-1 flex-wrap gap-2">
              <div>
                <PageTitle pageTitle="Subject Check-In" />
                <p
                  className={styles.pageHeaderSubtitle}
                  style={{ marginTop: "2px" }}
                >
                  Manage facility check-in for study subjects across all periods and pre-study processes.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <CommonButton
                  module={PERMISSION_MODULES.SUBJECT_CHECKIN}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                  style={{ height: "35px" }}
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton module={PERMISSION_MODULES.SUBJECT_CHECKIN} action={PERMISSION_ACTIONS.SCAN} color="primary" className="px-2 fw-600">
                  <i className="ri-qr-scan-2-line fs-18 me-1" /> Scan Subject ID
                </CommonButton>
                {renderHeaderTabs}
              </div>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="row g-3 mb-3 px-0">
            <div className="col-md-4">
              <StatCard
                title="TOTAL EXPECTED"
                value={stats.total}
                subtext="Subjects enrolled in this period"
                icon={<i className="ri-group-line" />}
                bgColor="#ecfdf5"
                iconColor="#10b981"
                trendType="info"
                subtextIcon="ri-information-line"
              />
            </div>
            <div className="col-md-4">
              <StatCard
                title="CHECKED IN"
                value={stats.checkedIn}
                subtext="Currently inside the facility"
                icon={<i className="ri-login-box-line" />}
                bgColor="#eff6ff"
                iconColor="#3b82f6"
                trendType="success"
                subtextIcon="ri-arrow-left-down-line"
              />
            </div>
            <div className="col-md-4">
              <StatCard
                title="PENDING CHECK-IN"
                value={stats.pending}
                subtext="Yet to arrive at facility"
                icon={<i className="ri-time-line" />}
                bgColor="#fef2f2"
                iconColor="#ef4444"
                trendType="negative"
                subtextIcon="ri-error-warning-line"
              />
            </div>
          </div>

          {/* Table Card */}
          <div className={styles.listPanel}>
            <div
              className={styles.panelHeader}
              style={{ borderBottom: "none", alignItems: "center" }}
            >
              <h5 className="mb-0 fw-semibold fs-15 text-dark">
                <i className="ri-user-add-line me-2 text-primary fs-16" />
                Subject Check-In
              </h5>

              <CommonSearch onSearch={onSearch} searchText={searchText} />
            </div>

            {/* Period Tabs */}
            <div
              style={{
                padding: "0 1.75rem",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                flexWrap: "wrap",
                gap: 0,
              }}
            >
              {dynamicPeriods.map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    setActiveTab(t.key);
                    setCurrentPageNumber(1);
                  }}
                  style={{
                    padding: "12px 16px",
                    fontSize: 13.5,
                    fontWeight: activeTab === t.key ? 600 : 400,
                    color:
                      activeTab === t.key ? "var(--vz-primary)" : "#64748b",
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      activeTab === t.key
                        ? "2px solid var(--vz-primary)"
                        : "2px solid transparent",
                    marginBottom: -1,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {t.label}
                  {t.key === "1" && (
                    <span
                      style={{
                        display: "inline-block",
                        background: "var(--vz-primary)",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        borderRadius: 20,
                        padding: "1px 7px",
                        lineHeight: "16px",
                      }}
                    >
                      Active
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Common Table */}
            <div className="px-4 pb-3 pt-3" style={{ overflowX: "auto" }}>
              <CommonTableIndexWise
                columns={CheckInColumns(openCheckin, canassign)}
                data={data}
                loading={isLoading || isFetching}
                pageSize={isLoading || isFetching ? 2 : PAGE_SIZE}
                currentPage={currentPageNumber}
                totalRecords={data[0]?.total_count || 0}
                onPageChange={setCurrentPageNumber}
                moduleName={PERMISSION_MODULES.SUBJECT_CHECKIN}
                actions={{
                  onView: (row) => {
                    navigate(SUBJECT_CHECKIN_PATHS.VIEW.replace(":id", String(row.id)));
                  },
                  onDelete: (row) => {
                    setRecordToDelete(row);
                    setDeleteModalOpen(true);
                  },
                }}
              />
            </div>
          </div>
        </Container>

        {/* Action Modals */}
        <CheckInModal
          isOpen={checkinOpen}
          subject={activeSubject}
          onConfirm={confirmCheckin}
          onClose={() => {
            setCheckinOpen(false);
            setActiveSubject(null);
          }}
          isCheckinLoading={UpdateMutation.isPending}
          isChecklistLoading={ChecklistQuery.isLoading}
          questionlist={checkinQuestions}
          checkinDate={currentPeriod?.checkin_date}
        />

        {/* Delete Modal */}
        {deleteModalOpen && (
          <DeleteModal
            isopen={deleteModalOpen}
            recordId={recordToDelete?.id}
            recordName={recordToDelete?.subjectName}
            toggle={() => {
              setDeleteModalOpen(false);
              setRecordToDelete(null);
              setShowDeleteReason(false);
            }}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={() => setShowDeleteReason((p) => !p)}
            onDeleteClick={handleDeleteConfirm}
          />
        )}

        <FilePreviewModal
          isOpen={previewOpen}
          file={previewFile}
          size="xl"
          onClose={() => {
            setPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      </div>
    </div>
  );
};

export default SubjectCheckinList;
